import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#syncOrphanFeesOnLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#rowForLine(line);
    let linkedFeeKeys = cartItemRowToRemove?.dataset.linkedFeeKeys
      ? cartItemRowToRemove.dataset.linkedFeeKeys.split('|').filter(Boolean)
      : [];

    if (this.#hasRemainingCustomDesignParentRows(cartItemRowToRemove)) {
      linkedFeeKeys = [];
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      lineItemKey: cartItemRowToRemove?.dataset.key || '',
      linkedFeeKeys,
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const linkedFeeKeys = Array.isArray(config.linkedFeeKeys) ? config.linkedFeeKeys.filter(Boolean) : [];
    const shouldRemoveLinkedFees = quantity === 0 && config.lineItemKey && linkedFeeKeys.length > 0;
    const body = shouldRemoveLinkedFees
      ? JSON.stringify({
          updates: Object.fromEntries([config.lineItemKey, ...linkedFeeKeys].map((key) => [key, 0])),
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        })
      : JSON.stringify({
          line: line,
          quantity: quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });
    const cartEndpoint = shouldRemoveLinkedFees ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;

    this.#debugCustomDesignFees('cart quantity payload', {
      action: config.action,
      line,
      quantity,
      lineItemKey: config.lineItemKey || '',
      linkedFeeKeys,
      endpoint: cartEndpoint,
    });

    cartTotal?.shimmer();

    fetch(`${cartEndpoint}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        let parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        parsedResponseText = await this.#syncCustomDesignHelperQuantities(parsedResponseText, sectionsToUpdate);
        parsedResponseText = await this.#removeOrphanCustomDesignFees(parsedResponseText, sectionsToUpdate);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.querySelector(`cart-quantity-selector-component input[data-cart-line="${line}"]`)?.closest('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Finds a rendered cart row by its Shopify cart line number.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowForLine(line) {
    return this.refs.cartItemRows.find((row) => Number(row.dataset.line || 0) === Number(line));
  }

  /**
   * @param {HTMLTableRowElement | undefined} rowToRemove
   * @returns {boolean}
   */
  #hasRemainingCustomDesignParentRows(rowToRemove) {
    if (rowToRemove?.dataset.inknfiberCustomDesignLine !== 'true') return false;

    const designId = rowToRemove.dataset.customDesignId;
    if (!designId) return false;

    return this.refs.cartItemRows.some(
      (row) =>
        row !== rowToRemove
        && row.dataset.inknfiberCustomDesignLine === 'true'
        && row.dataset.customDesignId === designId
    );
  }

  /**
   * @param {Object} item
   * @returns {boolean}
   */
  #isCustomDesignFeeItem(item) {
    const properties = item?.properties || {};

    return properties._inknfiber_fee_item === 'true'
      || properties['_inknfiber_fee_item'] === 'true'
      || properties._hidden_helper_line === 'true'
      || properties['_hidden_helper_line'] === 'true'
      || properties._custom_design_fee_line === 'true'
      || properties['_custom_design_fee_line'] === 'true'
      || properties._upload_fee_line === 'true'
      || properties['_upload_fee_line'] === 'true'
      || properties._hidden_fee_line === 'true'
      || properties['_hidden_fee_line'] === 'true'
      || Boolean(properties._adjustment_for || properties['_adjustment_for'])
      || Boolean(properties._adjustment_type || properties['_adjustment_type'])
      || item?.handle === 'custom-design-upload-fee'
      || item?.handle === 'artwork-setup-fee'
      || item?.handle === 'ai-design-generation-fee'
      || item?.handle === 'dtf-transfer-price-adjustment-product'
      || item?.handle === 'artwork-design-charge'
      || item?.product_title === 'Custom Design Upload Fee'
      || item?.product_title === 'Artwork Setup Fee'
      || item?.product_title === 'AI Design Generation Fee'
      || item?.product_title === 'DTF transfer price adjustment product'
      || item?.product_title === 'Artwork Design Charge';
  }

  /**
   * @param {Object} cart
   * @returns {number}
   */
  #visibleCartItemCount(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      return this.#isCustomDesignFeeItem(item) ? sum : sum + Number(item.quantity || 0);
    }, 0);
  }

  /**
   * Keeps Custom Designs one-cent helper lines aligned with the current parent line quantities.
   * @param {Object} cart
   * @param {Set<string>} sectionsToUpdate
   * @returns {Promise<Object>}
   */
  async #syncCustomDesignHelperQuantities(cart, sectionsToUpdate) {
    const groups = new Map();

    for (const item of cart.items || []) {
      if (!this.#isCustomDesignRelatedItem(item)) continue;

      const designId = this.#customDesignIdForItem(item);
      if (!designId) continue;

      if (!groups.has(designId)) {
        groups.set(designId, {
          totalQuantity: 0,
          artworkPriceCents: 0,
          productCorrectionCents: 0,
          artworkHelperKey: '',
          artworkHelperQuantity: 0,
          correctionHelperKey: '',
          correctionHelperQuantity: 0,
          setupHelperKeys: [],
        });
      }

      const group = groups.get(designId);
      if (this.#isCustomDesignFeeItem(item)) {
        const feeType = this.#customDesignFeeTypeForItem(item);
        if (feeType === 'artwork_design_charge') {
          group.artworkHelperKey = item.key || '';
          group.artworkHelperQuantity = Number(item.quantity || 0);
        } else if (feeType === 'product_price_correction') {
          group.correctionHelperKey = item.key || '';
          group.correctionHelperQuantity = Number(item.quantity || 0);
        } else if (feeType === 'artwork_setup_fee' || feeType === 'custom_design_placement_fee') {
          if (item.key) group.setupHelperKeys.push(item.key);
        }
        continue;
      }

      if (!this.#isCustomDesignParentItem(item)) continue;

      const quantity = Number(item.quantity || 0);
      const properties = item.properties || {};
      const artworkPriceCents = this.#centsFromProperty(
        properties._custom_design_line_artwork_price_cents
          || properties._custom_design_artwork_price_cents
          || properties._custom_design_artwork_price
      );
      const configuredUnitCents = this.#centsFromProperty(properties._custom_design_line_configured_unit_cents)
        || this.#centsFromProperty(properties._custom_design_line_configured_total) / Math.max(1, quantity);
      const shopifyUnitCents = this.#centsFromProperty(properties._custom_design_line_shopify_unit_cents)
        || Number(item.final_price || item.price || 0);

      group.totalQuantity += quantity;
      group.artworkPriceCents = artworkPriceCents || group.artworkPriceCents;
      group.productCorrectionCents += Math.max(0, Math.round(configuredUnitCents - shopifyUnitCents)) * quantity;
    }

    const updates = {};
    for (const group of groups.values()) {
      const expectedArtworkQuantity = Math.max(0, Math.round(group.artworkPriceCents * group.totalQuantity));
      const expectedCorrectionQuantity = Math.max(0, Math.round(group.productCorrectionCents));

      if (group.artworkHelperKey && group.artworkHelperQuantity !== expectedArtworkQuantity) {
        updates[group.artworkHelperKey] = expectedArtworkQuantity;
      }

      if (group.correctionHelperKey && group.correctionHelperQuantity !== expectedCorrectionQuantity) {
        updates[group.correctionHelperKey] = expectedCorrectionQuantity;
      }

      for (const key of group.setupHelperKeys) {
        updates[key] = 0;
      }
    }

    if (!Object.keys(updates).length) return cart;

    this.#debugCustomDesignFees('cart sync updating custom design helper quantities', { updates });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
    const responseText = await response.text();
    const updatedCart = JSON.parse(responseText);

    if (updatedCart.errors) return cart;
    return updatedCart;
  }

  /**
   * Removes hidden fee lines when their matching custom shirt design is gone.
   * @param {Object} cart
   * @param {Set<string>} sectionsToUpdate
   * @returns {Promise<Object>}
   */
  async #removeOrphanCustomDesignFees(cart, sectionsToUpdate) {
    const parentDesignIds = new Set(
      (cart.items || [])
        .filter((item) => !this.#isCustomDesignFeeItem(item))
        .map((item) => this.#customDesignIdForItem(item))
        .filter(Boolean)
    );
    const parentTransferIds = new Set(
      (cart.items || [])
        .filter((item) => !this.#isCustomDesignFeeItem(item) && item.properties?.['DTF Transfer ID'])
        .map((item) => item.properties['DTF Transfer ID'])
    );
    const parentDtfSheetIds = new Set(
      (cart.items || [])
        .filter((item) => !this.#isCustomDesignFeeItem(item) && (item.properties?.['DTF Sheet ID'] || item.properties?._inknfiber_dtf_sheet_id))
        .map((item) => item.properties['DTF Sheet ID'] || item.properties._inknfiber_dtf_sheet_id)
    );
    const orphanFeeItems = (cart.items || []).filter((item) => {
      if (!this.#isCustomDesignFeeItem(item)) return false;

      const parentDesignId = this.#customDesignIdForItem(item);
      const parentTransferId = item.properties?._inknfiber_parent_transfer_id;
      const parentDtfSheetId = item.properties?._design_id;
      const adjustmentFor = item.properties?._adjustment_for || '';

      if (adjustmentFor === 'DTF Film Builder') return !parentDtfSheetId || !parentDtfSheetIds.has(parentDtfSheetId);
      if (adjustmentFor === 'DTF Transfers By Size') return !parentTransferId || !parentTransferIds.has(parentTransferId);
      if (adjustmentFor === 'Custom Designs') return !parentDesignId || !parentDesignIds.has(parentDesignId);
      if (parentTransferId) return !parentTransferIds.has(parentTransferId);
      return !parentDesignId || !parentDesignIds.has(parentDesignId);
    });

    if (!orphanFeeItems.length) return cart;

    this.#debugCustomDesignFees('cart sync removing orphan fee lines', {
      orphanFeeKeys: orphanFeeItems.map((item) => item.key),
      parentDesignIds: Array.from(parentDesignIds),
      parentTransferIds: Array.from(parentTransferIds),
      parentDtfSheetIds: Array.from(parentDtfSheetIds),
    });

    const body = JSON.stringify({
      updates: Object.fromEntries(orphanFeeItems.map((item) => [item.key, 0])),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
    const responseText = await response.text();
    const updatedCart = JSON.parse(responseText);

    if (updatedCart.errors) return cart;
    return updatedCart;
  }

  /**
   * @param {Object} item
   * @returns {string}
   */
  #customDesignIdForItem(item) {
    const properties = item?.properties || {};

    return properties._inknfiber_parent_design_id
      || properties._custom_design_id
      || properties['Design ID']
      || properties._design_id
      || '';
  }

  /**
   * @param {Object} item
   * @returns {boolean}
   */
  #isCustomDesignParentItem(item) {
    const properties = item?.properties || {};

    return properties._inknfiber_builder_type === 'custom_design'
      && properties._inknfiber_line_role === 'parent';
  }

  /**
   * @param {Object} item
   * @returns {boolean}
   */
  #isCustomDesignRelatedItem(item) {
    const properties = item?.properties || {};

    return properties._inknfiber_builder_type === 'custom_design'
      || properties._adjustment_for === 'Custom Designs'
      || Boolean(properties._custom_design_id || properties._inknfiber_parent_design_id || properties['Design ID']);
  }

  /**
   * @param {Object} item
   * @returns {string}
   */
  #customDesignFeeTypeForItem(item) {
    const properties = item?.properties || {};
    const adjustmentType = String(properties._adjustment_type || '').toLowerCase();

    if (properties._inknfiber_fee_type) return String(properties._inknfiber_fee_type);
    if (adjustmentType.includes('artwork') || adjustmentType.includes('design price')) return 'artwork_design_charge';
    if (adjustmentType.includes('product price')) return 'product_price_correction';
    return '';
  }

  /**
   * @param {string | number | undefined | null} value
   * @returns {number}
   */
  #centsFromProperty(value) {
    if (value === undefined || value === null || value === '') return 0;
    if (typeof value === 'number') return Math.max(0, Math.round(value));

    const rawValue = String(value).trim();
    if (/^\d+$/.test(rawValue)) return Number(rawValue);

    const numericValue = Number(rawValue.replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(numericValue)) return 0;
    return Math.max(0, Math.round(numericValue * 100));
  }

  /**
   * @param {string} message
   * @param {Object} details
   */
  #debugCustomDesignFees(message, details = {}) {
    if (new URLSearchParams(window.location.search).get('designer_debug') !== '1') return;
    console.debug('[InkNfiber custom design fee cart]', message, details);
  }

  async #syncOrphanFeesOnLoad() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      const sectionsToUpdate = new Set([this.sectionId]);
      const helperSyncedCart = await this.#syncCustomDesignHelperQuantities(cart, sectionsToUpdate);
      const syncedCart = await this.#removeOrphanCustomDesignFees(helperSyncedCart, sectionsToUpdate);

      if (syncedCart === cart || !syncedCart.sections?.[this.sectionId]) return;

      this.dispatchEvent(
        new CartUpdateEvent(syncedCart, this.sectionId, {
          itemCount: this.#visibleCartItemCount(syncedCart),
          source: 'cart-items-component',
          sections: syncedCart.sections,
        })
      );
      morphSection(this.sectionId, syncedCart.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
    } catch (error) {
      this.#debugCustomDesignFees('cart sync orphan fee check failed', { error: error?.message || error });
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
